import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from '@gatsbyjs/reach-router'
import { Subscribe } from '@react-rxjs/core'
import React, { FC, useCallback, useMemo } from 'react'
import { firstValueFrom } from 'rxjs'

import {
  latestAccessTokenExpiration$,
  refreshAccessToken,
  useAccessTokenExpiration,
} from '../../../lib/auth/expiration'
import { usePageUrl } from '../../../lib/pages'
import Modal from '../../Modal'

const padTime = (value: number) => value.toString().padStart(2, '0')

const SessionExpirationModalContent: FC = () => {
  const { loginWithRedirect } = useAuth0()
  const location = useLocation()
  const { navigate } = usePageUrl()
  const { status, expiresAt } = useAccessTokenExpiration()

  const isOpen = useMemo(() => status === 'expired' || status === 'expiringSoon', [status])
  const timeLeft = Math.floor((expiresAt - Date.now()) / 1000) // purposely avoiding useMemo
  const timer = useMemo(
    () => `${padTime(Math.floor(timeLeft / 60))}:${padTime(timeLeft % 60)}`,
    [timeLeft],
  )

  const continueBrowsing = useCallback(async () => {
    if (status !== 'expiringSoon') {
      return
    }
    if (typeof window === 'undefined') {
      return
    }
    return firstValueFrom(refreshAccessToken())
  }, [status])

  const resumeSession = useCallback(() => {
    if (status !== 'expired') {
      return
    }
    loginWithRedirect({
      appState: {
        returnTo: location ? `${location.pathname}${location.search}` : '',
      },
    }).catch(() => navigate('/login'))
  }, [location, status, navigate, loginWithRedirect])

  const onClose = useMemo(
    () => (status === 'expired' ? undefined : continueBrowsing),
    [status, continueBrowsing],
  )

  const buttonStyles = 'text-lg font-medium text-white bg-brand-orange py-2 px-5 rounded-4xl'
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        status === 'expiringSoon'
          ? 'Are you still here?'
          : status === 'expired'
            ? 'Continue where you left off'
            : null
      }
      content={
        <p className="text-base text-text-primary">
          {status === 'expiringSoon' ? (
            <>
              Your logged session will be paused due to inactivity. You may resume it when
              you&apos;re back.
            </>
          ) : status === 'expired' ? (
            <>
              Your session was paused due to inactivity.
              <br /> Ready to keep browsing?
            </>
          ) : null}
        </p>
      }
      cta={
        <React.Fragment>
          {status === 'expiringSoon' && (
            <p className="text-base text-text-primary mr-auto">
              Pausing in <b>{timer}</b>
            </p>
          )}
          {status === 'expiringSoon' ? (
            <button className={buttonStyles} onClick={continueBrowsing}>
              Continue browsing
            </button>
          ) : status === 'expired' ? (
            <button className={buttonStyles} onClick={resumeSession}>
              Resume session
            </button>
          ) : null}
        </React.Fragment>
      }
    />
  )
}

export const SessionExpirationModal: FC = () => {
  // avoid SSR errors since Suspense not supported in this ReactDOMServer version
  // https://legacy.reactjs.org/docs/error-decoder.html/?invariant=294
  if (typeof window === 'undefined') {
    return null
  }
  return (
    <Subscribe source$={latestAccessTokenExpiration$} fallback={null}>
      <SessionExpirationModalContent />
    </Subscribe>
  )
}
